/**
 * Puente contenido → dirección de arte: qué icono, qué color y qué retrato le tocan a
 * cada sala y a cada personaje de un caso.
 *
 * Hace falta porque las dos capas no comparten vocabulario: los packs de tema traen
 * salas propias («Caballerizas», «Sala de Control», «Mirador») y `design/rooms.ts` trae
 * ocho iconos con nombres del set inicial del MVP §5. Aquí se casan los dos, sin
 * inventar tokens de color: la paleta sale entera de `ROOMS` (docs/art-direction.md).
 *
 * **Color por posición, no por nombre.** El color se asigna por el índice de la sala
 * dentro del caso, así que dos salas de un mismo caso nunca comparten color — que es lo
 * que necesita el jugador. El icono sí va por nombre, y es el que distingue de verdad
 * (en escala de grises el color desaparece; el icono no).
 *
 * Las salas son las **cabeceras de columna** de la rejilla y las personas van dentro de
 * las casillas, así que cada persona necesita además una etiqueta cortísima (una o dos
 * letras) que quepa en una anotación a lápiz de 12 px y que sirva de atajo de teclado.
 */

import {
  BedDouble,
  Binoculars,
  BookOpen,
  Boxes,
  Briefcase,
  ChefHat,
  Church,
  CircleDot,
  Clapperboard,
  Coffee,
  DoorOpen,
  Film,
  Flower2,
  Landmark,
  Mic,
  Monitor,
  Package,
  Presentation,
  Scissors,
  Sofa,
  SquareParking,
  Truck,
  Umbrella,
  Utensils,
  Warehouse,
  Waves,
  Wheat,
  type LucideIcon,
} from 'lucide-react';

import type { PortraitDef } from '../design/portraits';
import { PORTRAITS } from '../design/portraits';
import { ROOMS } from '../design/rooms';

/**
 * Icono por nombre de sala, cubriendo las 24 salas de los tres packs de
 * `@murdoku/content` más el set inicial de `design/rooms.ts`. Las claves están
 * normalizadas (sin tildes, en minúsculas) para que una tilde perdida en un pack no
 * deje una sala sin icono.
 */
const ICONS_BY_ROOM: Record<string, LucideIcon> = {
  // Set inicial (docs/MVP.md §5) — también lo usa el tema de relleno del motor.
  biblioteca: BookOpen,
  cocina: ChefHat,
  sotano: Warehouse,
  terraza: Umbrella,
  salon: Sofa,
  jardin: Flower2,
  despacho: Briefcase,
  buhardilla: Boxes,

  // casa-senorial
  invernadero: Flower2,
  capilla: Church,
  caballerizas: Wheat,
  'sala de billar': CircleDot,

  // rodaje-serie
  plato: Clapperboard,
  camerinos: DoorOpen,
  'sala de control': Monitor,
  catering: Utensils,
  'sala de montaje': Film,
  'almacen de atrezo': Package,
  'caravana de produccion': Truck,
  'taller de vestuario': Scissors,

  // congreso-hotel
  'salon de actos': Presentation,
  'suite real': BedDouble,
  spa: Waves,
  cafeteria: Coffee,
  aparcamiento: SquareParking,
  'sala de prensa': Mic,
  cocinas: ChefHat,
  mirador: Binoculars,
};

/**
 * Iconos de reserva para salas que no estén en la tabla (un pack nuevo, o el tema de
 * relleno del motor con «Sala 1», «Sala 2»…). Se reparten por posición para que sigan
 * siendo distinguibles entre sí dentro del caso.
 */
const FALLBACK_ICONS: readonly LucideIcon[] = [
  Landmark,
  DoorOpen,
  Boxes,
  CircleDot,
  Package,
  Waves,
  Wheat,
  Film,
];

/** Paleta de sala: la de la dirección de arte, en su orden, sin duplicar valores. */
const ROOM_PALETTE = ROOMS.map((room) => ({ colorVar: room.colorVar, fg: room.fg }));

function normalize(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

/**
 * `true` si la sala tiene icono propio en la tabla y no cae en el de reserva. Lo usan
 * los tests para que ningún pack se quede sin cobertura al añadir salas nuevas.
 */
export function hasDedicatedIcon(name: string): boolean {
  return normalize(name) in ICONS_BY_ROOM;
}

export interface RoomVisual {
  /** Nombre tal y como lo escribe el pack de contenido. */
  name: string;
  icon: LucideIcon;
  colorVar: string;
  fg: 'ink' | 'paper';
  /** Tecla que selecciona esta sala en el selector: `1`…`n`. */
  shortcut: string;
}

/**
 * Aspecto de las salas de un caso, en el mismo orden que `case.rooms` (los índices del
 * motor sirven directamente como índice aquí).
 */
export function roomVisuals(roomNames: readonly string[]): RoomVisual[] {
  return roomNames.map((name, index) => {
    const palette = ROOM_PALETTE[index % ROOM_PALETTE.length];
    if (palette === undefined) throw new Error('la paleta de salas está vacía');
    const fallback = FALLBACK_ICONS[index % FALLBACK_ICONS.length] ?? Landmark;
    return {
      name,
      icon: ICONS_BY_ROOM[normalize(name)] ?? fallback,
      colorVar: palette.colorVar,
      fg: palette.fg,
      shortcut: String(index + 1),
    };
  });
}

/**
 * Retrato de un personaje. Se ancla al orden del personaje **en el pack**, no en el
 * caso, así que la misma persona lleva siempre la misma cara aunque `sampleTheme`
 * baraje el reparto.
 */
export function portraitFor(
  peopleInPack: readonly string[],
  name: string,
): PortraitDef {
  const index = peopleInPack.indexOf(name);
  const position = index >= 0 ? index : hash(name);
  const portrait = PORTRAITS[position % PORTRAITS.length];
  if (portrait === undefined) throw new Error('no hay retratos disponibles');
  return portrait;
}

function hash(value: string): number {
  let result = 0;
  for (let i = 0; i < value.length; i += 1) {
    result = (result * 31 + value.charCodeAt(i)) >>> 0;
  }
  return result;
}

export interface PersonVisual {
  name: string;
  portrait: PortraitDef;
  /**
   * Una o dos letras, únicas dentro del caso. Es lo que se pinta en una anotación a
   * lápiz, donde no cabe ni el retrato ni el nombre.
   */
  initials: string;
  /** Tecla que selecciona a esta persona en el selector: `1`…`n`. */
  shortcut: string;
}

/**
 * Aspecto de las personas de un caso, en el mismo orden que `case.people`.
 *
 * `peopleInPack` es el reparto completo del tema, en su orden original: de ahí sale el
 * retrato, para que la misma persona lleve siempre la misma cara aunque `sampleTheme`
 * baraje quién entra en cada caso.
 */
export function personVisuals(
  peopleInCase: readonly string[],
  peopleInPack: readonly string[],
): PersonVisual[] {
  const initials = uniqueInitials(peopleInCase);
  return peopleInCase.map((name, index) => ({
    name,
    portrait: portraitFor(peopleInPack, name),
    initials: initials[index] ?? '?',
    shortcut: String(index + 1),
  }));
}

/**
 * Iniciales únicas dentro del caso: la primera letra, y si dos personas la comparten,
 * tantas letras como hagan falta. Los tres packs usan nombres de A a H, así que casi
 * siempre es una sola letra; no conviene depender de eso.
 */
function uniqueInitials(names: readonly string[]): string[] {
  const result = names.map((name) => firstLetters(name, 1));
  for (let length = 2; length <= 4; length += 1) {
    const counts = new Map<string, number>();
    for (const label of result) counts.set(label, (counts.get(label) ?? 0) + 1);
    if ([...counts.values()].every((count) => count === 1)) break;
    names.forEach((name, index) => {
      if ((counts.get(result[index] ?? '') ?? 0) > 1) {
        result[index] = firstLetters(name, length);
      }
    });
  }
  return result;
}

function firstLetters(name: string, length: number): string {
  const letters = name.replace(/[^\p{L}]/gu, '');
  return (letters.slice(0, length) || '?').toUpperCase();
}

/**
 * Índice que corresponde a una tecla: un número lo elige por posición, y una letra elige
 * el primer nombre que empieza por ella (MVP §6, navegación por teclado). Con varios
 * nombres por la misma letra, cada pulsación avanza al siguiente.
 */
export function matchByKey(
  names: readonly string[],
  key: string,
  current: number | null,
): number | null {
  if (/^[1-9]$/.test(key)) {
    const index = Number(key) - 1;
    return index < names.length ? index : null;
  }
  if (key.length !== 1 || !/\p{L}/u.test(key)) return null;

  const wanted = normalize(key);
  const matches = names.flatMap((name, index) =>
    normalize(name).startsWith(wanted) ? [index] : [],
  );
  if (matches.length === 0) return null;

  const after = current === null ? -1 : matches.indexOf(current);
  return matches[(after + 1) % matches.length] ?? null;
}
